import * as SheetPrimitive from "@radix-ui/react-dialog";
import { useEffect, useId, useRef } from "react";
import { usePageContext } from "vike-react/usePageContext";

import { Sheet, SheetOverlay, SheetPortal, SheetTitle } from "@/components/ui/sheet";
import { useChat } from "@/contexts/ChatContext";
import { useIsMobile } from "@/hooks/use-mobile";

import ChatComposer from "./ChatComposer";
import ChatHeader from "./ChatHeader";
import ChatMessageList from "./ChatMessageList";
import { PANEL_TITLE } from "./chatCopy";

/**
 * TWO FORM FACTORS, TWO MECHANISMS. This is not an oversight to be simplified into one modal.
 *
 * Below 768px the panel is a full-screen sheet and modal is CORRECT: Radix Dialog (which
 * `ui/sheet.tsx` wraps) gives the focus trap, `Escape` and the body scroll lock for free, and a
 * panel covering the whole viewport should not leave the page behind it operable.
 *
 * At and above 768px the panel is a plain positioned `div` with `aria-modal="false"`, and it CANNOT
 * use `Sheet`. Verified constraint, not a preference: `SheetContent` renders `SheetOverlay`
 * unconditionally with a hard-coded `fixed inset-0 z-50 bg-black/80` and exposes no suppression
 * prop, and Radix Dialog additionally locks body scroll and traps focus. All three contradict UA-02,
 * whose entire point is that the store page stays legible and scrollable while the visitor asks
 * "الأسعار هنا كويسة؟". There is no prop-level escape from it.
 *
 * The mobile branch therefore renders `SheetPrimitive.Content` directly rather than `SheetContent`,
 * because `SheetContent` also ships its own close button and the custom header owns the ONE close
 * affordance.
 *
 * `Escape` CLOSES THE PANEL AND DOES NOT STOP THE RUN. Closing must never cancel a turn: the run
 * completes, the assistant turn persists, and D-04's unread dot surfaces it. Cancellation has
 * exactly one trigger, the stop button, and that is what makes WEB-07's semantics legible.
 */
export default function ChatPanel() {
  const { open, closePanel } = useChat();
  const pageContext = usePageContext();
  const isMobile = useIsMobile();
  const pathname = pageContext.urlPathname || "";
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement | null>(null);

  const isAdmin = pathname.startsWith("/admin") || pathname.startsWith("/moderator");

  // The docked side follows the launcher. A panel that grows out of the opposite corner from the
  // button that opened it reads as an unrelated overlay rather than as that button's surface.
  const isMerchant = pathname.startsWith("/merchant");
  const anchorSide = isMerchant ? "left-4" : "right-4";
  const transformOrigin = isMerchant ? "bottom left" : "bottom right";

  // Desktop focus, hand-managed because there is no Radix here to do it. On open, focus moves INTO
  // the panel and lands on the composer's textarea, which carries `data-chat-composer-input`, so the
  // visitor can start typing immediately. The card is the fallback target: if the selector ever fails
  // to match, focus still enters the panel so `Escape` has somewhere to fire from and the visitor is
  // not left focused on the launcher behind an open panel.
  useEffect(() => {
    if (!open || isMobile || isAdmin) return;
    const card = cardRef.current;
    if (card === null) return;
    const composer = card.querySelector<HTMLElement>("[data-chat-composer-input]");
    (composer ?? card).focus();
  }, [open, isMobile, isAdmin]);

  // NO FOCUS TRAP, deliberately: the page behind is intentionally usable (UA-02). `Escape` closes and
  // returns focus to the launcher, which is located by attribute rather than by a shared ref because
  // the launcher is a sibling under `BrowserWidgets` and lifting state purely to pass a DOM node
  // across would be the more coupled design.
  useEffect(() => {
    if (!open || isMobile || isAdmin) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      closePanel();
      if (typeof document === "undefined") return;
      document.querySelector<HTMLElement>("[data-chat-launcher]")?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, isMobile, isAdmin, closePanel]);

  // Mirrors the launcher's route scope (UA-03): no consumer chat surface inside the admin app.
  if (isAdmin) return null;
  if (!open) return null;

  // DOM ORDER IS THE CONTRACT: header, then the message list, then the composer. Everything inside
  // the panel is positioned with logical utilities so the reading order and the tab order cannot
  // diverge under RTL.
  const body = (
    <>
      <ChatHeader isMobile={isMobile} onClose={closePanel} titleId={titleId} />

      {/* The scrollport, its 48px stick geometry, the turn loop, the skeleton rows, the empty state
          and the panel's one visually-hidden announcer all live inside this component. The list owns
          the scrollport rather than receiving one from here because the autoscroll rule is direct
          arithmetic on that element, and splitting the measurement from the markup across two files
          is how the two drift. */}
      <ChatMessageList />

      {/* LAST CHILD, SO IT IS THE LAST TAB STOP. DOM order is the tab order: header, list, composer,
          and within the composer the textarea then the action button -- which is what puts the stop
          control one `Tab` from where focus already is while a run streams. */}
      <ChatComposer />
    </>
  );

  if (isMobile) {
    return (
      <Sheet
        open
        onOpenChange={(next) => {
          if (!next) closePanel();
        }}
      >
        <SheetPortal>
          <SheetOverlay />
          <SheetPrimitive.Content
            // `100dvh` and NOT `100vh`: on mobile the URL bar is inside `vh` and outside `dvh`, so
            // `100vh` puts the composer under the browser chrome on exactly the devices this form
            // factor exists for.
            className="fixed inset-x-0 bottom-0 z-50 flex h-[100dvh] w-full flex-col rounded-none bg-[var(--ys-bg)] p-0 font-arabic shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom data-[state=closed]:duration-300 data-[state=open]:duration-500"
            // Radix names the dialog from its Title. The visible title lives in the header, so this
            // one is visually hidden rather than duplicated on screen. `aria-describedby={undefined}`
            // opts out of the description Radix would otherwise warn about.
            aria-describedby={undefined}
          >
            <SheetTitle className="sr-only">{PANEL_TITLE}</SheetTitle>
            {body}
          </SheetPrimitive.Content>
        </SheetPortal>
      </Sheet>
    );
  }

  return (
    <div
      ref={cardRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      tabIndex={-1}
      // `bottom-[88px]` = 16px viewport inset + the launcher's 56px disc + a 16px gap, so the card
      // sits directly above the button it grew out of. NO BACKDROP AND NO DIMMING: the page behind
      // stays readable and scrollable, which is the whole of UA-02.
      className={`fixed bottom-[88px] ${anchorSide} z-50 flex h-[600px] max-h-[calc(100vh-104px)] w-[400px] flex-col overflow-hidden rounded-[var(--ys-r-lg)] border border-[var(--ys-rule)] bg-[var(--ys-bg)] font-arabic shadow-[var(--ys-shadow-4)] focus:outline-none motion-safe:animate-scale-in motion-reduce:animate-in motion-reduce:fade-in-0 motion-reduce:duration-200`}
      // It should look like it came OUT OF the launcher, so the origin is the launcher's corner and
      // mirrors with the side flip. The `motion-reduce:` pair above swaps the scale for a plain
      // opacity fade at the call site, using utilities already in the build rather than a new
      // keyframe.
      style={{ transformOrigin, animationTimingFunction: "var(--ys-ease)" }}
    >
      {body}
    </div>
  );
}
